import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  ArrowLeft, GraduationCap, Target, TrendingUp, BookOpen, Award,
  Calendar, CheckCircle2, Clock, Star, Plus, Trash2, Edit,
  Brain, Sparkles, FileText, Building, MapPin, ChevronRight,
  Loader2, AlertCircle, User, Briefcase, Trophy, Zap, MessageCircle,
  ClipboardList, BarChart3, Search, Scale, Globe, Heart, X
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADE_OPTIONS = ['A*', 'A', 'B', 'C', 'D', 'E'];
const A_LEVEL_SUBJECTS = [
  'Mathematics', 'Further Mathematics', 'Physics', 'Chemistry', 'Biology',
  'Economics', 'Business', 'Psychology', 'History', 'Geography',
  'English Literature', 'English Language', 'Politics', 'Sociology',
  'Computer Science', 'Art', 'Music', 'Drama', 'French', 'Spanish', 'German',
  'Religious Studies', 'Philosophy', 'Law', 'Media Studies'
];

const EdpiesPathways = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [profileCompleteness, setProfileCompleteness] = useState(0);
  const [scorecard, setScorecard] = useState(null);
  
  // Universities state
  const [universities, setUniversities] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [matches, setMatches] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [deadlines, setDeadlines] = useState(null);
  
  // Progress state
  const [progress, setProgress] = useState(null);
  
  // Dialog states
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showPSDialog, setShowPSDialog] = useState(false);
  const [psGenerating, setPsGenerating] = useState(false);
  const [generatedPS, setGeneratedPS] = useState('');
  
  // Comparison state
  const [compareList, setCompareList] = useState([]);
  const [compareSearchQuery, setCompareSearchQuery] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadInitialData();
    // Check URL params for tab
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['overview', 'profile', 'universities', 'compare', 'application', 'interview', 'analytics', 'tasks'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    await Promise.all([
      loadProfile(),
      loadUniversities(),
      loadShortlist(),
      loadTasks(),
      loadDeadlines(),
      loadProgress()
    ]);
    setLoading(false);
  };

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/pathways/profile`, { headers });
      setProfile(response.data.profile);
      setProfileCompleteness(response.data.completeness);
      
      if (response.data.has_profile) {
        const scorecardRes = await axios.get(`${API}/pathways/profile/competency-scorecard`, { headers });
        setScorecard(scorecardRes.data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const loadUniversities = async () => {
    try {
      const response = await axios.get(`${API}/pathways/universities`, { headers });
      setUniversities(response.data.universities);
    } catch (error) {
      console.error('Error loading universities:', error);
    }
  };

  const loadShortlist = async () => {
    try {
      const response = await axios.get(`${API}/pathways/shortlist`, { headers });
      setShortlist(response.data.shortlist);
    } catch (error) {
      console.error('Error loading shortlist:', error);
    }
  };

  const loadMatches = async () => {
    try {
      const response = await axios.post(`${API}/pathways/match-universities`, {}, { headers });
      setMatches(response.data);
    } catch (error) {
      toast.error('Complete your profile to get university matches');
    }
  };

  const loadTasks = async () => {
    try {
      const response = await axios.get(`${API}/pathways/tasks`, { headers });
      setTasks(response.data.tasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const loadDeadlines = async () => {
    try {
      const response = await axios.get(`${API}/pathways/deadlines`, { headers });
      setDeadlines(response.data);
    } catch (error) {
      console.error('Error loading deadlines:', error);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API}/pathways/progress`, { headers });
      setProgress(response.data);
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const saveProfile = async (updatedProfile) => {
    try {
      await axios.post(`${API}/pathways/profile`, updatedProfile, { headers });
      toast.success('Profile saved!');
      loadProfile();
      loadMatches();
    } catch (error) {
      toast.error('Failed to save profile');
    }
  };

  const addToShortlist = async (uniId, courseId) => {
    try {
      await axios.post(`${API}/pathways/shortlist`, {
        university_id: uniId,
        course_id: courseId,
        status: 'considering'
      }, { headers });
      toast.success('Added to shortlist!');
      loadShortlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    }
  };

  const removeFromShortlist = async (uniId, courseId) => {
    try {
      await axios.delete(`${API}/pathways/shortlist/${uniId}/${courseId}`, { headers });
      toast.success('Removed from shortlist');
      loadShortlist();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const generatePersonalStatement = async (formData) => {
    setPsGenerating(true);
    try {
      const response = await axios.post(`${API}/pathways/personal-statement/generate`, formData, { headers });
      setGeneratedPS(response.data.draft);
      toast.success('Personal statement draft generated!');
    } catch (error) {
      toast.error('Failed to generate personal statement');
    }
    setPsGenerating(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100/50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-500">Loading Eds UniGuide™...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100/50 to-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-500 hover:text-slate-900"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </Button>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/pathways/admin')}
              className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
              data-testid="pathways-admin-btn"
            >
              <Edit className="h-4 w-4 mr-1" />
              Manage Unis
            </Button>
            <Badge className="bg-indigo-500/20 text-indigo-600 border-indigo-500/30">
              Year 13 University Prep
            </Badge>
          </div>
        </div>

        {/* Title Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 mb-4">
            <GraduationCap className="h-5 w-5 text-indigo-600" />
            <span className="text-indigo-600 font-medium">Eds UniGuide™</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            From Ambition to Admission
          </h1>
          <p className="text-slate-500 max-w-2xl mx-auto">
            Your AI-powered university counsellor - personalized guidance for your entire UCAS journey
          </p>
        </div>

        {/* Progress Overview */}
        {progress && (
          <Card className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-indigo-500/20 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Application Progress</h3>
                <span className="text-2xl font-bold text-indigo-600">{progress.overall_progress}%</span>
              </div>
              <Progress value={progress.overall_progress} className="h-3 mb-4" />
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{profileCompleteness}%</p>
                  <p className="text-xs text-slate-500">Profile Complete</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{shortlist.length}/5</p>
                  <p className="text-xs text-slate-500">UCAS Choices</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{progress.personal_statement.drafted ? '✓' : '–'}</p>
                  <p className="text-xs text-slate-500">Personal Statement</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs - Mobile Optimized */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Mobile: Horizontally scrollable tabs */}
          <div className="mb-6 -mx-4 px-4 md:mx-0 md:px-0">
            <TabsList className="inline-flex md:grid md:grid-cols-8 gap-1 bg-slate-100 p-1 w-auto md:w-full overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
              <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-600 text-xs whitespace-nowrap flex-shrink-0 px-3">
                <BarChart3 className="h-4 w-4 mr-1 hidden sm:block" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="profile" className="data-[state=active]:bg-indigo-600 text-xs whitespace-nowrap flex-shrink-0 px-3">
                <User className="h-4 w-4 mr-1 hidden sm:block" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="universities" className="data-[state=active]:bg-indigo-600 text-xs whitespace-nowrap flex-shrink-0 px-3">
                <Building className="h-4 w-4 mr-1 hidden sm:block" />
                Unis
              </TabsTrigger>
              <TabsTrigger value="compare" className="data-[state=active]:bg-teal-600 text-xs whitespace-nowrap flex-shrink-0 px-3">
                <Scale className="h-4 w-4 mr-1 hidden sm:block" />
                Compare
              </TabsTrigger>
              <TabsTrigger value="application" className="data-[state=active]:bg-indigo-600 text-xs whitespace-nowrap flex-shrink-0 px-3">
                <FileText className="h-4 w-4 mr-1 hidden sm:block" />
                Apply
              </TabsTrigger>
              <TabsTrigger value="interview" className="data-[state=active]:bg-green-600 text-xs whitespace-nowrap flex-shrink-0 px-3">
                <MessageCircle className="h-4 w-4 mr-1 hidden sm:block" />
                Interview
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-purple-600 text-xs whitespace-nowrap flex-shrink-0 px-3">
                <TrendingUp className="h-4 w-4 mr-1 hidden sm:block" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="tasks" className="data-[state=active]:bg-indigo-600 text-xs whitespace-nowrap flex-shrink-0 px-3">
                <ClipboardList className="h-4 w-4 mr-1 hidden sm:block" />
                Tasks
              </TabsTrigger>
            </TabsList>
          </div>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Competency Scorecard */}
              {scorecard && (
                <Card className="bg-slate-100 border-slate-200/50">
                  <CardHeader>
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-amber-600" />
                      Competency Scorecard
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(scorecard.scores).filter(([k]) => k !== 'overall').map(([key, value]) => (
                      <div key={key}>
                        <div className="flex justify-between mb-1">
                          <span className="text-slate-600 capitalize">{key.replace('_', ' ')}</span>
                          <span className="text-slate-900 font-semibold">{value}%</span>
                        </div>
                        <Progress value={value} className="h-2" />
                      </div>
                    ))}
                    <div className="pt-4 border-t border-slate-200">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-900 font-semibold">Overall Score</span>
                        <span className="text-2xl font-bold text-indigo-600">{scorecard.scores.overall}%</span>
                      </div>
                    </div>
                    
                    {scorecard.oxbridge_readiness && (
                      <div className={`p-4 rounded-lg ${scorecard.oxbridge_readiness.ready ? 'bg-green-500/10 border border-green-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                        <p className={`font-semibold ${scorecard.oxbridge_readiness.ready ? 'text-green-600' : 'text-amber-600'}`}>
                          {scorecard.oxbridge_readiness.ready ? '✓ Oxbridge Ready' : '⚡ Oxbridge Readiness'}
                        </p>
                        {scorecard.oxbridge_readiness.gaps.length > 0 && (
                          <ul className="mt-2 text-sm text-slate-500">
                            {scorecard.oxbridge_readiness.gaps.map((gap, i) => (
                              <li key={i}>• {gap}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* UCAS Shortlist */}
              <Card className="bg-slate-100 border-slate-200/50">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Star className="h-5 w-5 text-amber-600" />
                    Your UCAS Shortlist
                  </CardTitle>
                  <CardDescription className="text-slate-500">
                    {shortlist.length}/5 choices selected
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {shortlist.length === 0 ? (
                    <div className="text-center py-8">
                      <Building className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-500">No universities added yet</p>
                      <Button 
                        className="mt-4" 
                        onClick={() => setActiveTab('universities')}
                      >
                        Browse Universities
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {shortlist.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-100">
                          <div>
                            <p className="text-slate-900 font-medium">{item.university_name}</p>
                            <p className="text-sm text-slate-500">{item.course_name}</p>
                          </div>
                          <Badge className={
                            item.status === 'offer' ? 'bg-green-500/20 text-green-600' :
                            item.status === 'applied' ? 'bg-blue-500/20 text-blue-600' :
                            'bg-slate-500/20 text-slate-500'
                          }>
                            {item.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Key Deadlines */}
              <Card className="bg-slate-100 border-slate-200/50">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-rose-600" />
                    Key UCAS Deadlines
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {deadlines && (
                    <div className="space-y-3">
                      {Object.entries(deadlines.deadlines['2025-2026']).slice(0, 5).map(([key, date]) => (
                        <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-slate-100">
                          <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className="text-slate-900 font-medium">{date}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recommendations */}
              {scorecard?.recommendations && (
                <Card className="bg-slate-100 border-slate-200/50">
                  <CardHeader>
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-purple-600" />
                      Ed's Picks
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {scorecard.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-600">
                          <ChevronRight className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* PROFILE TAB */}
          <TabsContent value="profile">
            <ProfileEditor 
              profile={profile} 
              onSave={saveProfile}
              completeness={profileCompleteness}
            />
          </TabsContent>

          {/* UNIVERSITIES TAB */}
          <TabsContent value="universities">
            <UniversityBrowser 
              universities={universities}
              shortlist={shortlist}
              matches={matches}
              onAddToShortlist={addToShortlist}
              onRemoveFromShortlist={removeFromShortlist}
              onLoadMatches={loadMatches}
              profileCompleteness={profileCompleteness}
            />
          </TabsContent>

          {/* COMPARE TAB */}
          <TabsContent value="compare">
            <UniversityComparison
              universities={universities}
              shortlist={shortlist}
              compareList={compareList}
              setCompareList={setCompareList}
              searchQuery={compareSearchQuery}
              setSearchQuery={setCompareSearchQuery}
            />
          </TabsContent>

          {/* APPLICATION TAB */}
          <TabsContent value="application">
            <ApplicationTools 
              profile={profile}
              onGeneratePS={generatePersonalStatement}
              psGenerating={psGenerating}
              generatedPS={generatedPS}
            />
          </TabsContent>

          {/* INTERVIEW TAB */}
          <TabsContent value="interview">
            <InterviewCoaching shortlist={shortlist} />
          </TabsContent>

          {/* ANALYTICS TAB */}
          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          {/* TASKS TAB */}
          <TabsContent value="tasks">
            <TaskManager tasks={tasks} deadlines={deadlines} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Profile Editor Component
const ProfileEditor = ({ profile, onSave, completeness }) => {
  const [editProfile, setEditProfile] = useState(profile || {
    academic: { predicted_grades: {}, a_level_subjects: [], gcse_grades: {} },
    extracurriculars: [],
    preferences: { course_interests: [], career_aspirations: [], preferred_locations: [] },
    skills: [],
    achievements: []
  });
  const [newSubject, setNewSubject] = useState('');
  const [newGrade, setNewGrade] = useState('A');

  const addSubject = () => {
    if (newSubject && !editProfile.academic.a_level_subjects.includes(newSubject)) {
      const updated = {
        ...editProfile,
        academic: {
          ...editProfile.academic,
          a_level_subjects: [...editProfile.academic.a_level_subjects, newSubject],
          predicted_grades: {
            ...editProfile.academic.predicted_grades,
            [newSubject]: newGrade
          }
        }
      };
      setEditProfile(updated);
      setNewSubject('');
    }
  };

  const removeSubject = (subject) => {
    const newGrades = { ...editProfile.academic.predicted_grades };
    delete newGrades[subject];
    setEditProfile({
      ...editProfile,
      academic: {
        ...editProfile.academic,
        a_level_subjects: editProfile.academic.a_level_subjects.filter(s => s !== subject),
        predicted_grades: newGrades
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Progress Card */}
      <Card className="bg-indigo-500/10 border-indigo-500/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-900 font-medium">Profile Completeness</span>
            <span className="text-indigo-600 font-bold">{completeness}%</span>
          </div>
          <Progress value={completeness} className="h-2" />
        </CardContent>
      </Card>

      {/* Academic Section */}
      <Card className="bg-slate-100 border-slate-200/50">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-600" />
            Academic Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-600">A-Level Subjects & Predicted Grades</Label>
            <div className="flex gap-2 mt-2">
              <Select value={newSubject} onValueChange={setNewSubject}>
                <SelectTrigger className="bg-slate-100 border-slate-200 text-slate-900">
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {A_LEVEL_SUBJECTS.map(sub => (
                    <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={newGrade} onValueChange={setNewGrade}>
                <SelectTrigger className="w-24 bg-slate-100 border-slate-200 text-slate-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRADE_OPTIONS.map(g => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={addSubject} size="icon">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-3">
              {editProfile.academic.a_level_subjects.map(subject => (
                <Badge 
                  key={subject} 
                  className="bg-blue-500/20 text-blue-600 px-3 py-1 flex items-center gap-2"
                >
                  {subject}: {editProfile.academic.predicted_grades[subject]}
                  <button onClick={() => removeSubject(subject)} className="hover:text-red-600">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preferences Section */}
      <Card className="bg-slate-100 border-slate-200/50">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Target className="h-5 w-5 text-green-600" />
            Preferences & Goals
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-600">Course Interests (comma separated)</Label>
            <Input
              className="bg-slate-100 border-slate-200 text-slate-900 mt-1"
              placeholder="e.g., Computer Science, Engineering, Mathematics"
              value={editProfile.preferences.course_interests?.join(', ') || ''}
              onChange={(e) => setEditProfile({
                ...editProfile,
                preferences: {
                  ...editProfile.preferences,
                  course_interests: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                }
              })}
            />
          </div>
          <div>
            <Label className="text-slate-600">Career Aspirations</Label>
            <Input
              className="bg-slate-100 border-slate-200 text-slate-900 mt-1"
              placeholder="e.g., Software Engineer, Data Scientist"
              value={editProfile.preferences.career_aspirations?.join(', ') || ''}
              onChange={(e) => setEditProfile({
                ...editProfile,
                preferences: {
                  ...editProfile.preferences,
                  career_aspirations: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                }
              })}
            />
          </div>
          <div className="flex items-center gap-4">
            <Label className="text-slate-600">Interested in Oxbridge?</Label>
            <Button
              variant={editProfile.preferences.oxbridge_interest ? "default" : "outline"}
              size="sm"
              onClick={() => setEditProfile({
                ...editProfile,
                preferences: {
                  ...editProfile.preferences,
                  oxbridge_interest: !editProfile.preferences.oxbridge_interest
                }
              })}
            >
              {editProfile.preferences.oxbridge_interest ? 'Yes' : 'No'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Skills Section */}
      <Card className="bg-slate-100 border-slate-200/50">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-600" />
            Skills & Achievements
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-600">Key Skills (comma separated)</Label>
            <Input
              className="bg-slate-100 border-slate-200 text-slate-900 mt-1"
              placeholder="e.g., Problem solving, Leadership, Programming"
              value={editProfile.skills?.join(', ') || ''}
              onChange={(e) => setEditProfile({
                ...editProfile,
                skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
              })}
            />
          </div>
          <div>
            <Label className="text-slate-600">Notable Achievements</Label>
            <Textarea
              className="bg-slate-100 border-slate-200 text-slate-900 mt-1"
              placeholder="e.g., Duke of Edinburgh Gold, Math Olympiad Bronze"
              value={editProfile.achievements?.join('\n') || ''}
              onChange={(e) => setEditProfile({
                ...editProfile,
                achievements: e.target.value.split('\n').filter(Boolean)
              })}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Button 
        onClick={() => onSave(editProfile)}
        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
      >
        Save Profile
      </Button>
    </div>
  );
};

// University Browser Component
const UniversityBrowser = ({ universities, shortlist, matches, onAddToShortlist, onRemoveFromShortlist, onLoadMatches, profileCompleteness }) => {
  const [view, setView] = useState('all'); // all, matches, compare
  const [selectedUni, setSelectedUni] = useState(null);
  const [compareList, setCompareList] = useState([]);

  const isInShortlist = (uniId, courseId) => {
    return shortlist.some(s => s.university_id === uniId && s.course_id === courseId);
  };

  const isInCompare = (uniId) => compareList.some(u => u.id === uniId);

  const toggleCompare = (uni) => {
    if (isInCompare(uni.id)) {
      setCompareList(compareList.filter(u => u.id !== uni.id));
    } else if (compareList.length < 3) {
      setCompareList([...compareList, uni]);
    }
  };

  return (
    <div className="space-y-6">
      {/* View Toggle & Match Button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Button 
            variant={view === 'all' ? 'default' : 'outline'}
            onClick={() => setView('all')}
            size="sm"
          >
            All Universities
          </Button>
          <Button 
            variant={view === 'matches' ? 'default' : 'outline'}
            onClick={() => { setView('matches'); onLoadMatches(); }}
            disabled={profileCompleteness < 50}
            size="sm"
          >
            <Sparkles className="h-4 w-4 mr-1" />
            AI Matches
          </Button>
          <Button 
            variant={view === 'compare' ? 'default' : 'outline'}
            onClick={() => setView('compare')}
            size="sm"
            className={compareList.length > 0 ? 'border-indigo-500 text-indigo-600' : ''}
          >
            <Scale className="h-4 w-4 mr-1" />
            Compare ({compareList.length}/3)
          </Button>
        </div>
        <Badge className="bg-slate-100 text-slate-600">
          {shortlist.length}/5 Selected
        </Badge>
      </div>

      {/* Compare Mode */}
      {view === 'compare' && (
        <div className="space-y-6">
          {compareList.length === 0 ? (
            <Card className="bg-slate-100 border-slate-200/50 border-dashed">
              <CardContent className="p-8 text-center">
                <Scale className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Compare Universities Side-by-Side</h3>
                <p className="text-slate-500 mb-4">Select up to 3 universities to compare rankings, acceptance rates, and courses</p>
                <Button onClick={() => setView('all')} variant="outline">
                  Browse Universities
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Comparison Table */}
              <div className="overflow-x-auto -mx-4 px-4">
                <div className="min-w-[600px]">
                  <div className={`grid gap-4`} style={{ gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)` }}>
                    {/* Header Row */}
                    <div className="text-slate-500 font-medium p-3"></div>
                    {compareList.map(uni => (
                      <Card key={uni.id} className="bg-slate-100 border-slate-200/50">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-slate-900 text-sm">{uni.name}</h3>
                              <p className="text-xs text-slate-500">{uni.location}</p>
                            </div>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-6 w-6 text-slate-500 hover:text-red-600"
                              onClick={() => toggleCompare(uni)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          {uni.oxbridge && <Badge className="mt-2 bg-amber-500/20 text-amber-600 text-xs">Oxbridge</Badge>}
                        </CardContent>
                      </Card>
                    ))}
                    
                    {/* UK Ranking */}
                    <div className="text-slate-600 font-medium p-3 bg-slate-100 rounded-l-lg flex items-center gap-2">
                      <Award className="h-4 w-4 text-amber-600" /> UK Ranking
                    </div>
                    {compareList.map(uni => (
                      <div key={`rank-${uni.id}`} className="p-3 bg-slate-100 text-center">
                        <span className="text-2xl font-bold text-slate-900">#{uni.ranking_uk}</span>
                      </div>
                    ))}
                    
                    {/* World Ranking */}
                    <div className="text-slate-600 font-medium p-3 flex items-center gap-2">
                      <Globe className="h-4 w-4 text-blue-600" /> World Ranking
                    </div>
                    {compareList.map(uni => (
                      <div key={`world-${uni.id}`} className="p-3 text-center">
                        <span className="text-lg font-semibold text-slate-600">#{uni.ranking_world || 'N/A'}</span>
                      </div>
                    ))}
                    
                    {/* Acceptance Rate */}
                    <div className="text-slate-600 font-medium p-3 bg-slate-100 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-600" /> Acceptance Rate
                    </div>
                    {compareList.map(uni => (
                      <div key={`accept-${uni.id}`} className="p-3 bg-slate-100 text-center">
                        <span className={`text-lg font-semibold ${
                          uni.acceptance_rate >= 50 ? 'text-green-600' : 
                          uni.acceptance_rate >= 30 ? 'text-amber-600' : 'text-red-600'
                        }`}>{uni.acceptance_rate}%</span>
                      </div>
                    ))}
                    
                    {/* Student Satisfaction */}
                    <div className="text-slate-600 font-medium p-3 flex items-center gap-2">
                      <Heart className="h-4 w-4 text-pink-600" /> Student Satisfaction
                    </div>
                    {compareList.map(uni => (
                      <div key={`sat-${uni.id}`} className="p-3 text-center">
                        <span className="text-lg font-semibold text-slate-600">{uni.student_satisfaction || 'N/A'}%</span>
                      </div>
                    ))}
                    
                    {/* Graduate Prospects */}
                    <div className="text-slate-600 font-medium p-3 bg-slate-100 flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-purple-600" /> Graduate Prospects
                    </div>
                    {compareList.map(uni => (
                      <div key={`grad-${uni.id}`} className="p-3 bg-slate-100 text-center">
                        <span className="text-lg font-semibold text-slate-600">{uni.graduate_prospects || 'N/A'}%</span>
                      </div>
                    ))}

                    {/* Type */}
                    <div className="text-slate-600 font-medium p-3 flex items-center gap-2">
                      <Building className="h-4 w-4 text-indigo-600" /> University Type
                    </div>
                    {compareList.map(uni => (
                      <div key={`type-${uni.id}`} className="p-3 text-center">
                        <Badge className="bg-indigo-500/20 text-indigo-600 text-xs">
                          {uni.type?.replace('_', ' ') || 'University'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <Button onClick={() => setView('all')} variant="outline" className="w-full">
                <Plus className="h-4 w-4 mr-2" /> Add More Universities to Compare
              </Button>
            </>
          )}
        </div>
      )}

      {view === 'matches' && matches && (
        <div className="space-y-6">
          {/* Reach Universities */}
          <div>
            <h3 className="text-lg font-semibold text-red-600 mb-3 flex items-center gap-2">
              <Target className="h-5 w-5" /> Reach ({matches.recommendations.reach.length})
            </h3>
            <div className="grid gap-3">
              {matches.recommendations.reach.map((m, i) => (
                <UniversityMatchCard 
                  key={i} 
                  match={m} 
                  onAdd={onAddToShortlist}
                  inShortlist={isInShortlist(m.university_id, m.course_id)}
                />
              ))}
            </div>
          </div>
          
          {/* Match Universities */}
          <div>
            <h3 className="text-lg font-semibold text-amber-600 mb-3 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" /> Match ({matches.recommendations.match.length})
            </h3>
            <div className="grid gap-3">
              {matches.recommendations.match.map((m, i) => (
                <UniversityMatchCard 
                  key={i} 
                  match={m} 
                  onAdd={onAddToShortlist}
                  inShortlist={isInShortlist(m.university_id, m.course_id)}
                />
              ))}
            </div>
          </div>
          
          {/* Safety Universities */}
          <div>
            <h3 className="text-lg font-semibold text-green-600 mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" /> Safety ({matches.recommendations.safety.length})
            </h3>
            <div className="grid gap-3">
              {matches.recommendations.safety.map((m, i) => (
                <UniversityMatchCard 
                  key={i} 
                  match={m} 
                  onAdd={onAddToShortlist}
                  inShortlist={isInShortlist(m.university_id, m.course_id)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {view === 'all' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {universities.map(uni => (
            <Card 
              key={uni.id}
              className={`bg-slate-100 border-slate-200/50 hover:border-indigo-500/30 transition-all cursor-pointer ${isInCompare(uni.id) ? 'ring-2 ring-indigo-500' : ''}`}
              onClick={() => setSelectedUni(selectedUni?.id === uni.id ? null : uni)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-slate-900">{uni.name}</h3>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {uni.location}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant={isInCompare(uni.id) ? "default" : "ghost"}
                      className={`h-7 w-7 ${isInCompare(uni.id) ? 'bg-indigo-600' : 'text-slate-500 hover:text-indigo-600'}`}
                      onClick={(e) => { e.stopPropagation(); toggleCompare(uni); }}
                      disabled={!isInCompare(uni.id) && compareList.length >= 3}
                      title="Add to compare"
                    >
                      <Scale className="h-4 w-4" />
                    </Button>
                    {uni.oxbridge && (
                      <Badge className="bg-amber-500/20 text-amber-600">Oxbridge</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 text-xs text-slate-500">
                  <span>UK Rank: #{uni.ranking_uk}</span>
                  <span>•</span>
                  <span>{uni.acceptance_rate}% acceptance</span>
                </div>
                
                {selectedUni?.id === uni.id && (
                  <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
                    <p className="text-sm text-slate-600 font-medium">Available Courses:</p>
                    {Object.entries(uni.courses || {}).map(([courseId, course]) => (
                      <div key={courseId} className="flex items-center justify-between p-2 rounded bg-slate-100">
                        <div>
                          <p className="text-sm text-slate-900">{course.name}</p>
                          <p className="text-xs text-slate-500">Typical offer: {course.typical_offer}</p>
                        </div>
                        <Button
                          size="sm"
                          variant={isInShortlist(uni.id, courseId) ? "secondary" : "default"}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isInShortlist(uni.id, courseId)) {
                              onAddToShortlist(uni.id, courseId);
                            }
                          }}
                          disabled={isInShortlist(uni.id, courseId) || shortlist.length >= 5}
                        >
                          {isInShortlist(uni.id, courseId) ? 'Added' : 'Add'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// University Match Card
const UniversityMatchCard = ({ match, onAdd, inShortlist }) => (
  <Card className={`border ${
    match.category === 'reach' ? 'border-red-500/30 bg-red-500/5' :
    match.category === 'match' ? 'border-amber-500/30 bg-amber-500/5' :
    'border-green-500/30 bg-green-500/5'
  }`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-slate-900">{match.university_name}</h4>
            {match.oxbridge && <Badge className="bg-amber-500/20 text-amber-600 text-xs">Oxbridge</Badge>}
          </div>
          <p className="text-slate-600">{match.course_name}</p>
          <div className="flex items-center gap-3 mt-2 text-sm">
            <span className="text-slate-500">Offer: {match.typical_offer}</span>
            <span className={`font-medium ${
              match.probability >= 60 ? 'text-green-600' :
              match.probability >= 35 ? 'text-amber-600' :
              'text-red-600'
            }`}>
              {match.probability}% chance
            </span>
          </div>
          {match.admissions_test && (
            <Badge className="mt-2 bg-purple-500/20 text-purple-600 text-xs">
              Requires {match.admissions_test}
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => !inShortlist && onAdd(match.university_id, match.course_id)}
          disabled={inShortlist}
          className={inShortlist ? 'bg-slate-200' : ''}
        >
          {inShortlist ? 'Added' : 'Add'}
        </Button>
      </div>
    </CardContent>
  </Card>
);

// University Comparison Component
const UniversityComparison = ({ universities, shortlist, compareList, setCompareList, searchQuery, setSearchQuery }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  
  const filteredUniversities = universities.filter(uni => 
    uni.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    !compareList.find(c => c.name === uni.name)
  );
  
  const addToCompare = (uni) => {
    if (compareList.length >= 4) {
      toast.error('You can compare up to 4 universities');
      return;
    }
    setCompareList([...compareList, uni]);
    toast.success(`Added ${uni.name} to comparison`);
  };
  
  const removeFromCompare = (uniName) => {
    setCompareList(compareList.filter(u => u.name !== uniName));
  };
  
  const comparisonFields = [
    { key: 'ranking', label: 'UK Ranking', icon: Trophy, format: (v) => v ? `#${v}` : '-' },
    { key: 'location', label: 'Location', icon: MapPin, format: (v) => v || '-' },
    { key: 'acceptance_rate', label: 'Acceptance Rate', icon: Target, format: (v) => v ? `${v}%` : '-' },
    { key: 'typical_offer', label: 'Typical Offer', icon: Award, format: (v) => v || '-' },
    { key: 'student_satisfaction', label: 'Student Satisfaction', icon: Heart, format: (v) => v ? `${v}%` : '-' },
    { key: 'graduate_prospects', label: 'Graduate Prospects', icon: Briefcase, format: (v) => v ? `${v}%` : '-' },
    { key: 'research_quality', label: 'Research Quality', icon: Star, format: (v) => v ? `${v}/5` : '-' },
    { key: 'campus_facilities', label: 'Campus Facilities', icon: Building, format: (v) => v || '-' },
    { key: 'tuition_fees', label: 'Annual Fees', icon: Scale, format: (v) => v ? `£${v.toLocaleString()}` : '-' },
    { key: 'international_students', label: 'International Students', icon: Globe, format: (v) => v ? `${v}%` : '-' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Scale className="h-5 w-5 text-teal-600" />
            Compare Universities
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Compare up to 4 universities side-by-side
          </p>
        </div>
        
        {compareList.length < 4 && (
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700">
                <Plus className="h-4 w-4 mr-2" />
                Add University
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white border-slate-200">
              <DialogHeader>
                <DialogTitle className="text-slate-900">Add University to Compare</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    placeholder="Search universities..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-100 border-slate-200 text-slate-900"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {/* Show shortlisted universities first */}
                  {shortlist.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-slate-500 mb-2 font-medium">YOUR SHORTLIST</p>
                      {shortlist.filter(uni => !compareList.find(c => c.name === uni.name)).map((uni) => (
                        <button
                          key={uni.name}
                          onClick={() => { addToCompare(uni); setShowAddDialog(false); setSearchQuery(''); }}
                          className="w-full flex items-center justify-between p-3 rounded-lg bg-slate-100 hover:bg-slate-100 text-left mb-2"
                        >
                          <div>
                            <p className="text-slate-900 text-sm font-medium">{uni.name}</p>
                            <p className="text-slate-500 text-xs">{uni.location}</p>
                          </div>
                          <Badge className="bg-indigo-500/20 text-indigo-600">Shortlisted</Badge>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-500 mb-2 font-medium">ALL UNIVERSITIES</p>
                  {filteredUniversities.slice(0, 10).map((uni) => (
                    <button
                      key={uni.name}
                      onClick={() => { addToCompare(uni); setShowAddDialog(false); setSearchQuery(''); }}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-slate-100 hover:bg-slate-100 text-left"
                    >
                      <div>
                        <p className="text-slate-900 text-sm font-medium">{uni.name}</p>
                        <p className="text-slate-500 text-xs">{uni.location} • Rank #{uni.ranking || '-'}</p>
                      </div>
                      <Plus className="h-4 w-4 text-slate-500" />
                    </button>
                  ))}
                  {filteredUniversities.length === 0 && (
                    <p className="text-slate-500 text-center py-4 text-sm">No universities found</p>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Comparison Table */}
      {compareList.length === 0 ? (
        <Card className="bg-slate-100 border-slate-200">
          <CardContent className="py-16 text-center">
            <Scale className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Start Comparing</h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Add universities to compare their rankings, entry requirements, satisfaction scores, and more.
            </p>
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => setShowAddDialog(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First University
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4">
          <div className="min-w-[640px]">
            {/* University Headers */}
            <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)` }}>
              <div className="p-4 bg-slate-100 rounded-lg">
                <p className="text-slate-500 text-sm font-medium">Comparing</p>
                <p className="text-slate-900 font-bold">{compareList.length} Universities</p>
              </div>
              {compareList.map((uni) => (
                <Card key={uni.name} className="bg-white border-slate-200 shadow-sm relative">
                  <button
                    onClick={() => removeFromCompare(uni.name)}
                    className="absolute top-2 right-2 p-1 rounded-full bg-slate-100 hover:bg-red-100 transition-colors"
                  >
                    <X className="h-4 w-4 text-slate-500 hover:text-slate-900" />
                  </button>
                  <CardContent className="p-4 pt-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mx-auto mb-3">
                      <Building className="h-6 w-6 text-slate-900" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm leading-tight mb-1">{uni.name}</h3>
                    <p className="text-slate-500 text-xs">{uni.location}</p>
                    {uni.ranking && (
                      <Badge className="mt-2 bg-teal-500/20 text-teal-600">Rank #{uni.ranking}</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Comparison Rows */}
            <div className="space-y-2">
              {comparisonFields.map((field, idx) => {
                const IconComponent = field.icon;
                return (
                  <div 
                    key={field.key} 
                    className={`grid gap-4 p-3 rounded-lg ${idx % 2 === 0 ? 'bg-slate-100' : 'bg-slate-100'}`}
                    style={{ gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)` }}
                  >
                    <div className="flex items-center gap-2">
                      <IconComponent className="h-4 w-4 text-slate-500" />
                      <span className="text-slate-600 text-sm font-medium">{field.label}</span>
                    </div>
                    {compareList.map((uni) => (
                      <div key={`${uni.name}-${field.key}`} className="text-center">
                        <span className="text-slate-900 font-medium text-sm">
                          {field.format(uni[field.key])}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Add More Button */}
            {compareList.length < 4 && (
              <div className="mt-6 text-center">
                <Button
                  variant="outline"
                  className="border-teal-500/50 text-teal-600 hover:bg-teal-500/10"
                  onClick={() => setShowAddDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another University ({4 - compareList.length} slots remaining)
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Tips */}
      {compareList.length > 0 && (
        <Card className="bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border-teal-500/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-teal-600 mt-0.5" />
              <div>
                <p className="text-slate-900 font-medium text-sm">Comparison Tips</p>
                <p className="text-slate-500 text-xs mt-1">
                  Consider not just rankings, but also location, student satisfaction, and graduate prospects. 
                  A "lower-ranked" university might be the perfect fit for your specific course and career goals.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Application Tools Component
const ApplicationTools = ({ profile, onGeneratePS, psGenerating, generatedPS }) => {
  const [psForm, setPsForm] = useState({
    course: '',
    universities: [],
    key_experiences: [],
    why_this_course: '',
    career_goals: '',
    style: 'balanced'
  });

  return (
    <div className="space-y-6">
      {/* Personal Statement Generator */}
      <Card className="bg-slate-100 border-slate-200/50">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-600" />
            AI Personal Statement Generator
          </CardTitle>
          <CardDescription className="text-slate-500">
            Generate a first draft based on your profile and course choice
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600">Course You're Applying For</Label>
              <Input
                className="bg-slate-100 border-slate-200 text-slate-900 mt-1"
                placeholder="e.g., Computer Science"
                value={psForm.course}
                onChange={(e) => setPsForm({...psForm, course: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-slate-600">Style</Label>
              <Select 
                value={psForm.style} 
                onValueChange={(v) => setPsForm({...psForm, style: v})}
              >
                <SelectTrigger className="bg-slate-100 border-slate-200 text-slate-900 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="academic">Academic Focus</SelectItem>
                  <SelectItem value="personal">Personal Story</SelectItem>
                  <SelectItem value="balanced">Balanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label className="text-slate-600">Why This Course?</Label>
            <Textarea
              className="bg-slate-100 border-slate-200 text-slate-900 mt-1"
              placeholder="What sparked your interest? Any defining moments?"
              value={psForm.why_this_course}
              onChange={(e) => setPsForm({...psForm, why_this_course: e.target.value})}
              rows={3}
            />
          </div>
          
          <div>
            <Label className="text-slate-600">Career Goals</Label>
            <Input
              className="bg-slate-100 border-slate-200 text-slate-900 mt-1"
              placeholder="What do you hope to achieve with this degree?"
              value={psForm.career_goals}
              onChange={(e) => setPsForm({...psForm, career_goals: e.target.value})}
            />
          </div>

          <Button
            onClick={() => onGeneratePS(psForm)}
            disabled={psGenerating || !psForm.course}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600"
          >
            {psGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Personal Statement
              </>
            )}
          </Button>

          {generatedPS && (
            <div className="mt-4 p-4 rounded-lg bg-slate-100 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-slate-900">Generated Draft</h4>
                <Badge className="bg-green-500/20 text-green-600">
                  {generatedPS.length} characters
                </Badge>
              </div>
              <div className="prose prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-slate-600 text-sm font-sans">
                  {generatedPS}
                </pre>
              </div>
              <p className="text-xs text-slate-500 mt-4">
                * This is a starting point. Personalize with your unique voice and have teachers review it.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Interview Prep */}
      <Card className="bg-slate-100 border-slate-200/50">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-600" />
            Interview Preparation
          </CardTitle>
          <CardDescription className="text-slate-500">
            Coming soon: AI-powered mock interviews and coaching
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" disabled className="w-full">
            <Clock className="h-4 w-4 mr-2" />
            Coming Soon
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Task Manager Component
const TaskManager = ({ tasks, deadlines }) => (
  <div className="space-y-6">
    <Card className="bg-slate-100 border-slate-200/50">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-rose-600" />
          Your Application Tasks
        </CardTitle>
      </CardHeader>
      <CardContent>
        {tasks.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No pending tasks!</p>
        ) : (
          <div className="space-y-3">
            {tasks.map((task, idx) => (
              <div 
                key={idx}
                className={`p-4 rounded-lg border ${
                  task.priority === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                  task.priority === 'high' ? 'bg-amber-500/10 border-amber-500/30' :
                  'bg-slate-100 border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900">{task.title}</h4>
                    <p className="text-sm text-slate-500">{task.description}</p>
                    {task.deadline && (
                      <p className="text-xs text-rose-600 mt-1 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Deadline: {task.deadline}
                      </p>
                    )}
                  </div>
                  <Badge className={
                    task.priority === 'critical' ? 'bg-red-500/20 text-red-600' :
                    task.priority === 'high' ? 'bg-amber-500/20 text-amber-600' :
                    'bg-slate-500/20 text-slate-500'
                  }>
                    {task.priority}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

// Interview Coaching Component
const InterviewCoaching = ({ shortlist }) => {
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [interviewType, setInterviewType] = useState('general');
  const [selectedUni, setSelectedUni] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/pathways/interview/history`, { headers });
      setHistory(response.data.sessions || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const startInterview = async () => {
    if (!selectedUni || !selectedCourse) {
      toast.error('Please select a university and course');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/pathways/interview/start`, {
        university_id: selectedUni,
        course_id: selectedCourse,
        interview_type: interviewType
      }, { headers });
      setSession(response.data);
      setCurrentQuestion(0);
      setAnswers({});
      setFeedback({});
      setResults(null);
      toast.success('Mock interview started!');
    } catch (error) {
      toast.error('Failed to start interview');
    }
    setLoading(false);
  };

  const submitAnswer = async () => {
    const question = session.questions[currentQuestion];
    const answer = answers[question.id] || '';
    
    if (!answer.trim()) {
      toast.error('Please enter an answer');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/pathways/interview/answer`, {
        session_id: session.session_id,
        question_id: question.id,
        answer: answer
      }, { headers });
      
      setFeedback(prev => ({...prev, [question.id]: response.data.feedback}));
      
      if (currentQuestion < session.questions.length - 1) {
        setCurrentQuestion(prev => prev + 1);
      }
      toast.success('Answer submitted!');
    } catch (error) {
      toast.error('Failed to submit answer');
    }
    setLoading(false);
  };

  const completeInterview = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/pathways/interview/complete?session_id=${session.session_id}`,
        {},
        { headers }
      );
      setResults(response.data);
      loadHistory();
      toast.success('Interview completed!');
    } catch (error) {
      toast.error('Failed to complete interview');
    }
    setLoading(false);
  };

  // Results View
  if (results) {
    return (
      <div className="space-y-6">
        <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20">
          <CardContent className="p-8 text-center">
            <Trophy className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Interview Complete!</h2>
            <p className="text-5xl font-bold text-green-600 mb-2">{results.overall_score}/10</p>
            <Badge className={`text-lg px-4 py-1 ${
              results.performance_band === 'Excellent' ? 'bg-green-500/20 text-green-600' :
              results.performance_band === 'Good' ? 'bg-blue-500/20 text-blue-600' :
              'bg-amber-500/20 text-amber-600'
            }`}>
              {results.performance_band}
            </Badge>
            <p className="text-slate-500 mt-4">{results.message}</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-100 border-slate-200/50">
          <CardHeader>
            <CardTitle className="text-slate-900">Question Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {results.answers?.map((a, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-slate-100 border border-slate-200">
                <p className="text-slate-900 font-medium mb-2">Q{idx + 1}: {a.question}</p>
                <p className="text-slate-500 text-sm mb-2">Your answer: {a.answer}</p>
                {a.feedback && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-green-600 font-semibold">Score: {a.feedback.score}/10</span>
                    </div>
                    <p className="text-sm text-slate-600">{a.feedback.overall_comment}</p>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Button onClick={() => { setSession(null); setResults(null); }} className="w-full">
          Start New Interview
        </Button>
      </div>
    );
  }

  // Active Interview View
  if (session) {
    const question = session.questions[currentQuestion];
    const questionFeedback = feedback[question?.id];

    return (
      <div className="space-y-6">
        <Card className="bg-slate-100 border-slate-200/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-slate-900">{session.university} - {session.course}</CardTitle>
                <CardDescription className="text-slate-500">
                  Question {currentQuestion + 1} of {session.questions.length}
                </CardDescription>
              </div>
              <Badge className="bg-green-500/20 text-green-600">{session.interview_type}</Badge>
            </div>
            <Progress value={((currentQuestion + 1) / session.questions.length) * 100} className="h-2 mt-4" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
              <Badge className="mb-2 bg-indigo-500/20 text-indigo-600">{question?.category}</Badge>
              <p className="text-slate-900 text-lg font-medium">{question?.question}</p>
            </div>

            <Textarea
              placeholder="Type your answer here... Take your time to think before responding."
              value={answers[question?.id] || ''}
              onChange={(e) => setAnswers({...answers, [question?.id]: e.target.value})}
              className="bg-slate-100 border-slate-200 text-slate-900 min-h-[150px]"
              disabled={!!questionFeedback}
            />

            {questionFeedback && (
              <Card className="bg-green-500/10 border-green-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-green-600 font-semibold">Score: {questionFeedback.score}/10</span>
                  </div>
                  <p className="text-slate-600 text-sm">{questionFeedback.overall_comment}</p>
                  {questionFeedback.improvements?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-amber-600 text-xs font-medium">To improve:</p>
                      <ul className="text-slate-500 text-xs">
                        {questionFeedback.improvements.map((imp, i) => (
                          <li key={i}>• {imp}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="flex gap-3">
              {!questionFeedback ? (
                <Button onClick={submitAnswer} disabled={loading} className="flex-1 bg-indigo-600">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Submit Answer
                </Button>
              ) : currentQuestion < session.questions.length - 1 ? (
                <Button onClick={() => setCurrentQuestion(prev => prev + 1)} className="flex-1">
                  Next Question <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={completeInterview} disabled={loading} className="flex-1 bg-green-600">
                  Complete Interview
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {session.tips && (
          <Card className="bg-amber-500/10 border-amber-500/20">
            <CardContent className="p-4">
              <h4 className="text-amber-600 font-semibold mb-2">Interview Tips</h4>
              <ul className="text-slate-600 text-sm space-y-1">
                {session.tips.slice(0, 4).map((tip, i) => (
                  <li key={i}>• {tip}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Start Interview View
  return (
    <div className="space-y-6">
      <Card className="bg-slate-100 border-slate-200/50">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-600" />
            AI Mock Interview
          </CardTitle>
          <CardDescription className="text-slate-500">
            Practice university interviews with AI-powered feedback
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-600">Select University from Shortlist</Label>
            <Select value={selectedUni} onValueChange={(v) => { setSelectedUni(v); setSelectedCourse(''); }}>
              <SelectTrigger className="bg-slate-100 border-slate-200 text-slate-900 mt-1">
                <SelectValue placeholder="Choose university" />
              </SelectTrigger>
              <SelectContent>
                {shortlist.map(item => (
                  <SelectItem key={`${item.university_id}-${item.course_id}`} value={item.university_id}>
                    {item.university_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedUni && (
            <div>
              <Label className="text-slate-600">Select Course</Label>
              <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                <SelectTrigger className="bg-slate-100 border-slate-200 text-slate-900 mt-1">
                  <SelectValue placeholder="Choose course" />
                </SelectTrigger>
                <SelectContent>
                  {shortlist.filter(s => s.university_id === selectedUni).map(item => (
                    <SelectItem key={item.course_id} value={item.course_id}>
                      {item.course_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-slate-600">Interview Type</Label>
            <Select value={interviewType} onValueChange={setInterviewType}>
              <SelectTrigger className="bg-slate-100 border-slate-200 text-slate-900 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General Interview</SelectItem>
                <SelectItem value="motivational">Motivational Questions</SelectItem>
                <SelectItem value="technical">Technical/Academic</SelectItem>
                <SelectItem value="oxbridge">Oxbridge Style</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            onClick={startInterview} 
            disabled={loading || !selectedUni || !selectedCourse}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageCircle className="h-4 w-4 mr-2" />}
            Start Mock Interview
          </Button>
        </CardContent>
      </Card>

      {/* Interview History */}
      {history.length > 0 && (
        <Card className="bg-slate-100 border-slate-200/50">
          <CardHeader>
            <CardTitle className="text-slate-900">Practice History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.slice(0, 5).map((s, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-100">
                  <div>
                    <p className="text-slate-900 text-sm font-medium">{s.university_name}</p>
                    <p className="text-slate-500 text-xs">{s.course_name} • {s.interview_type}</p>
                  </div>
                  <div className="text-right">
                    {s.status === 'completed' ? (
                      <Badge className="bg-green-500/20 text-green-600">{s.overall_score}/10</Badge>
                    ) : (
                      <Badge className="bg-amber-500/20 text-amber-600">In Progress</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Analytics Dashboard Component
const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/pathways/analytics/dashboard`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <Card className="bg-slate-100 border-slate-200/50">
        <CardContent className="p-8 text-center">
          <BarChart3 className="h-12 w-12 text-slate-500 mx-auto mb-4" />
          <p className="text-slate-500">Complete your profile to see analytics</p>
        </CardContent>
      </Card>
    );
  }

  const { profile_strength, shortlist_analysis, interview_readiness, grade_forecast, success_metrics, recommendations } = analytics;

  return (
    <div className="space-y-6">
      {/* Success Probability */}
      <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20">
        <CardContent className="p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Overall Success Probability</h3>
            <div className="relative w-32 h-32 mx-auto mb-4">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="64" cy="64" r="56" stroke="#334155" strokeWidth="12" fill="none" />
                <circle 
                  cx="64" cy="64" r="56" 
                  stroke="url(#gradient)" 
                  strokeWidth="12" 
                  fill="none"
                  strokeDasharray={`${success_metrics.overall_success_probability * 3.52} 352`}
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#a855f7" />
                    <stop offset="100%" stopColor="#ec4899" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold text-slate-900">{success_metrics.overall_success_probability}%</span>
              </div>
            </div>
            <p className="text-slate-500 text-sm">{grade_forecast.prediction}</p>
          </div>
        </CardContent>
      </Card>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-100 border-slate-200/50">
          <CardContent className="p-4 text-center">
            <User className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-900">{profile_strength.score}%</p>
            <p className="text-xs text-slate-500">Profile Strength</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-100 border-slate-200/50">
          <CardContent className="p-4 text-center">
            <BookOpen className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-900">{success_metrics.academic_strength}%</p>
            <p className="text-xs text-slate-500">Academic Strength</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-100 border-slate-200/50">
          <CardContent className="p-4 text-center">
            <MessageCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-900">{interview_readiness.average_score}/10</p>
            <p className="text-xs text-slate-500">Interview Score</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-100 border-slate-200/50">
          <CardContent className="p-4 text-center">
            <Target className="h-8 w-8 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-900">{shortlist_analysis.total}/5</p>
            <p className="text-xs text-slate-500">UCAS Choices</p>
          </CardContent>
        </Card>
      </div>

      {/* Shortlist Analysis */}
      <Card className="bg-slate-100 border-slate-200/50">
        <CardHeader>
          <CardTitle className="text-slate-900">Shortlist Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-3xl font-bold text-red-600">{shortlist_analysis.reach_count}</p>
              <p className="text-sm text-slate-500">Reach</p>
            </div>
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-3xl font-bold text-amber-600">{shortlist_analysis.match_count}</p>
              <p className="text-sm text-slate-500">Match</p>
            </div>
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-3xl font-bold text-green-600">{shortlist_analysis.safety_count}</p>
              <p className="text-sm text-slate-500">Safety</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grade Forecast */}
      <Card className="bg-slate-100 border-slate-200/50">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            Grade Forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-500">Confidence Level</span>
            <span className="text-purple-600 font-bold">{grade_forecast.confidence}%</span>
          </div>
          <Progress value={grade_forecast.confidence} className="h-2 mb-4" />
          <p className="text-slate-600 text-sm mb-4">{grade_forecast.prediction}</p>
          <div className="space-y-1">
            {grade_forecast.factors?.map((factor, i) => (
              <p key={i} className="text-xs text-slate-500 flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-green-600" /> {factor}
              </p>
            ))}
          </div>
          <div className="mt-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
            <p className="text-purple-600 text-sm">
              Estimated offers: <span className="font-bold">{grade_forecast.estimated_offers}</span> of {shortlist_analysis.total}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Ed's Picks */}
      <Card className="bg-slate-100 border-slate-200/50">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-600" />
            Ed's Picks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {recommendations?.map((rec, i) => (
              <li key={i} className="flex items-start gap-2 text-slate-600 text-sm">
                <ChevronRight className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                {rec}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default EdpiesPathways;
